import { useMemo } from 'react'
import { formatUserDisplayName } from '@/utils/formatters'
import { splitMentionSegments } from '@/utils/mentions'
import { cn } from '@/utils/cn'

interface MentionedUser {
  id: string
  display_name?: string | null
}

/**
 * Renders comment content, turning raw `@[uuid]` mention markup (see
 * src/utils/mentions.ts) into a resolved, highlighted "@Name". Names come
 * from `mentionedUsers`, which the backend resolves per-comment by exact id
 * (see CommentRead.mentioned_users) — not from a client-side user directory,
 * which would miss anyone outside whatever page of users happened to load.
 */
export function CommentText({
  content,
  mentionedUsers,
  className,
}: {
  content: string
  mentionedUsers?: MentionedUser[]
  className?: string
}) {
  const nameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of mentionedUsers ?? []) {
      map.set(user.id, formatUserDisplayName(user))
    }
    return map
  }, [mentionedUsers])

  const segments = useMemo(() => splitMentionSegments(content), [content])

  return (
    <p className={cn('text-sm whitespace-pre-wrap mt-0.5', className)}>
      {segments.map((segment, i) =>
        segment.type === 'text' ? (
          <span key={i}>{segment.value}</span>
        ) : (
          <span key={i} className="font-medium text-[#CF1F2E]">
            @{nameById.get(segment.userId) ?? 'Unknown user'}
          </span>
        )
      )}
    </p>
  )
}
